// Catalogue des niveaux de test (source de vérité miroir des levels/test/*.txt).
// Les .txt sont la source lisible ; ce catalogue les inline pour un import direct
// côté client (pas de fetch). Le test tests/levels-catalog.test.ts vérifie la
// synchro exacte avec les .txt (le spawn J2 « 2 » du multi, lot 4b, vit dans les deux),
// le format 28x16, et validateLevel === [] pour chacun. Les arènes 06/07
// (duel versus/coop) n'ont pas de .txt : le catalogue est leur source de vérité.
import type { MonkSpeed } from './types'
import type { WorldKey } from './worlds'

// monkSpeed OPTIONNEL : les niveaux de test embarqués n'en portent pas (défaut 'normal' à la
// lecture) ; seules les cartes de l'éditeur / campagnes résolues le renseignent (Task 3). smartMonks
// OPTIONNEL de même (IA maligne ; absent -> false = comportement historique via createGame).
export interface CatalogEntry { slug: string; title: string; rows: string[]; world: WorldKey; monkSpeed?: MonkSpeed; smartMonks?: boolean; monkPowers?: boolean; decor?: string }

// Légende (cf. Task 2) : . vide, # brique, = béton, H échelle, - corde,
// x fausse brique, I glace, s sable, T/t téléporteur, $ or, b bombe, j marteau,
// z tazer, m moine, 1 spawn J1, 2 spawn J2, 3 spawn J3, E échelle de sortie (cachée au départ).
// Les entrées BRUTES, dans l'ordre historique de création. L'ordre JOUÉ est plus bas
// (PARCOURS_ORDER) : c'est lui qui est exporté sous le nom TEST_LEVELS.
const CATALOG_RAW: CatalogEntry[] = [
  {
    slug: '01-premiers-pas',
    title: 'Premiers pas',
    world: 'classique',
    rows: [
      '..........................E.',
      '..........................E.',
      '..........................E.',
      '..........................E.',
      '..........................E.',
      '..........................E.',
      '..........................E.',
      '.....$.....$..............E.',
      '....####H####.............E.',
      '........H.................E.',
      '........H.................E.',
      '........H.................E.',
      '........H.................E.',
      '1..$....H...........$...2.E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: '02-la-corde-raide',
    title: 'La corde raide',
    world: 'prairie',
    rows: [
      '..........................E.',
      '..H............H..........E.',
      '..H............H..........E.',
      '..H------------H..........E.',
      '..H.....$......H..........E.',
      '..H............H....m.....E.',
      '..H............H..........E.',
      '..H..$.........H..........E.',
      '..H#####x######H..........E.',
      '..H.....$......H..........E.',
      '..H############H..........E.',
      '..H............H..........E.',
      '..H............H..........E.',
      '1.H..$.........H......2...E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: '03-creuse',
    title: 'Creuse !',
    world: 'classique',
    rows: [
      '..........................E.',
      '..........................E.',
      '..........................E.',
      '..........................E.',
      '.....m....................E.',
      '..........................E.',
      '.......$..................E.',
      '...#H####################.E.',
      '....H.....................E.',
      '....H.....$........m......E.',
      '....H.....................E.',
      '....H.....$...............E.',
      '....H.....................E.',
      '1...H.....$...........2...E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: '04-glace-et-sable',
    title: 'Glace et sable',
    world: 'grotte',
    rows: [
      '..........................E.',
      '..H.......................E.',
      '..H.......................E.',
      '..H.$...j...$.............E.',
      '..H###IIIII###s##########.E.',
      '..H...........s.....m.....E.',
      '..H...........s...........E.',
      '..H..........#.#..........E.',
      '..H...........###.........E.',
      '..H.m.........$...........E.',
      '..H.......................E.',
      '..H.......................E.',
      '..H.......................E.',
      '1.H...............2...$...E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: '05-les-moines-fous',
    title: 'Les moines fous',
    world: 'grotte',
    // ⚠️ LA MEUTE SE RÉVEILLE ICI (26/07). Le comportement malin — anticipation deux cases
    // devant le coureur, ligne de vue, mémoire de la dernière position, patrouille, bulles
    // ! / ? / z — existait déjà et n'était allumé que sur le 10. Mesuré en production :
    // 0,0 à 0,9 mort par joueur sur les sept premiers niveaux, on traversait sans mourir.
    //
    // Allumé d'ABORD partout, puis retiré des niveaux 02 à 04 après mesure : on a rejoué de
    // VRAIES courses de la famille contre les deux IA. Le 02 gagnait en 490 ticks et meurt
    // désormais à 369 ; le 04 gagnait en 564 et meurt à 122, soit dans le premier cinquième
    // du niveau. Trop tôt dans le parcours pour des enfants. La chasse commence donc au 05,
    // celui qui s'appelle « Les moines fous » — la difficulté monte avec le niveau.
    // (« lent » ne rattrape rien : le durcissement vient de l'anticipation, pas de la vitesse.)
    smartMonks: true,
    rows: [
      '..........................E.',
      '..H.......................E.',
      '..H..........H............E.',
      'T.H...$......H.m..$.......E.',
      '##H##########H###########.E.',
      '..H..........H............E.',
      '..H...b......H....$...m...E.',
      '##H##########H###########.E.',
      '..H..........H............E.',
      't.H......$...H.m..$.......E.',
      '##H##########H###########.E.',
      '..H..........H............E.',
      '..H..........H............E.',
      '1.H......$...H......2.....E.',
      '############################',
      '============================',
    ],
  },
  // ═══ LES TROIS MARCHES QUI MANQUAIENT AUX MONDES 1 ET 2 (30/07) ═══════════════════════════
  // Le monde du chantier n'avait que trois étapes avant son boss, et celui des glissades deux.
  // Surtout, aucune ne préparait ce que le boss suivant exige : « Les moines fous » demande de
  // PIÉGER un moine, et rien avant ne l'apprenait ; « Glace et sable » lâche la glace, le sable,
  // le marteau et deux moines dans la même carte. Même remède que pour l'eau et le manoir :
  // une idée par niveau.
  {
    slug: 'c1-le-piege',
    title: 'Le piège',
    world: 'classique',
    // LA LEÇON : creuser un trou pour qu'un moine y tombe, puis lui marcher sur la tête. C'est
    // exactement ce que « Les moines fous » réclame, et c'était appris nulle part.
    // La FAUSSE BRIQUE (colonne 7) coupe le couloir du milieu en deux : le franchir demande de
    // comprendre qu'on peut passer par la corde. Deuxième échelle à droite (col 22) pour que
    // chaque moitié reste joignable sans creuser — le filet de finissabilité l'exige.
    rows: [
      '..........................E.',
      '..H.$................$....E.',
      '..H######################.E.',
      '..H----------------.......E.',
      '..H.......................E.',
      '..H..$......$...m..$......E.',
      '..H####x##############H##.E.',
      '..H...................H...E.',
      '..H...................H...E.',
      '..H...$..........$....H...E.',
      '..H###################H##.E.',
      '..H...................H...E.',
      '..H...................H...E.',
      '1.H.2..........m......H...E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 'g1-patinoire',
    title: 'Patinoire',
    world: 'neige',
    // LA LEÇON : sur la glace on ne s'arrête pas où on veut. Chaque dalle est PERCÉE — glisser
    // trop loin, c'est tomber d'un étage. Les deux échelles verticales (col 13 et col 21)
    // traversent les trois dalles : sans elles, les îlots entre deux trous seraient des culs-de-sac.
    rows: [
      '..........................E.',
      '..H..$.......H..$....H.$..E.',
      '..HIIIII.IIIIHIII.IIIHIII.E.',
      '..H..........H.......H....E.',
      '..H..........H.......H....E.',
      '..H.$......$.H...$...H..$.E.',
      '..HIII.IIIIIIHIIIII.IHIII.E.',
      '..H..........H.......H....E.',
      '..H..........H.......H....E.',
      '..H...$......H.$.....H.$..E.',
      '..HIIIIIII.IIHIIII.IIHIII.E.',
      '..H..........H.......H....E.',
      '..H..........H.......H....E.',
      '1.H.2........H...m...H....E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 'g2-sables-mouvants',
    title: 'Sables mouvants',
    world: 'desert',
    // LA LEÇON : le sable avale et ne rend pas. On s'y enfonce sans pouvoir RIEN faire — ni
    // aller de côté, ni s'accrocher, ni creuser — jusqu'à retomber un étage plus bas.
    //
    // ⚠️ RÈGLE DURE, VERROUILLÉE PAR UN TEST : le bas d'un puits de sable doit donner sur du
    // VIDE. Une case de sable posée directement sur un solide fige le coureur POUR TOUJOURS
    // (shared/sim.ts : il cesse de s'enfoncer et sort de la fonction sans rien faire). Aucune
    // mort, aucun message : la partie est simplement perdue. Les deux puits (colonnes 7 et 22)
    // débouchent donc chacun sur une case vide.
    //
    // LA POCHE (rangée 5, colonnes 20 à 24) : le puits de la colonne 22 en est la SEULE entrée.
    // La brèche de la colonne 19 (rangée 6) la coupe du reste de l'étage, et le bord droit
    // (colonne 25) est le seul moyen d'en ressortir — on se laisse tomber jusqu'au sol. Le sable
    // n'est donc pas décoratif ici : son or est inatteignable autrement. Première version : la
    // poche n'avait aucune entrée du tout et le filet a signalé « or (23,5) » injoignable.
    rows: [
      '..........................E.',
      '..H.$.....$..H...$........E.',
      '..H####s#####H########s##.E.',
      '..H....s.....H........s...E.',
      '..H....s.....H........s...E.',
      '..H..$.......H..$......$..E.',
      '..H##########H#####.#####.E.',
      '..H..........H............E.',
      '..H..........H............E.',
      '..H.....$....H.......$....E.',
      '..H##########H###########.E.',
      '..H..........H............E.',
      '..H..........H............E.',
      '1.H.2........H.m......$...E.',
      '############################',
      '============================',
    ],
  },
  // ═══ MONDE DE L'USINE (31/07) — le cinquième monde du parcours ═══════════════════════════
  // Biome `usine`, jamais utilisé par le parcours : il ne servait qu'à la grande carte bonus
  // « La grande machine ». Le moteur sait faire une trentaine de mécaniques et le jeu n'en
  // montrait qu'une poignée ; ce monde ouvre les tapis, les conduits, les ressorts et les rampes.
  //
  // ⚠️ CHOIX ASSUMÉ : pas de boutons, pas de barrières, pas de téléporteurs, pas de sens uniques.
  // Le filet de finissabilité ne les modélise PAS — une carte qui en dépendrait serait déclarée
  // infinissable alors qu'elle se joue très bien, et la seule sortie serait une exemption. Or une
  // exemption exige qu'un vrai joueur ait fini la carte, ce qu'aucune carte écrite aujourd'hui ne
  // peut prouver. On construit donc avec ce que le filet sait vérifier.
  {
    slug: 'u1-tapis-roulant',
    title: "Le tapis roulant",
    world: 'usine',
    // LA LEÇON : le sol avance tout seul. On marche dessus comme sur une dalle, mais on dérive —
    // viser une pièce demande de partir plus tôt, et s'arrêter au bord ne suffit plus.
    // Les deux tapis vont en sens contraires pour que l'aller et le retour ne se ressemblent pas.
    rows: [
      '..........................E.',
      '..H..$.......H...$.....$..E.',
      '..H>>>>>>>>>>H>>>>>>>>>>>.E.',
      '..H..........H............E.',
      '..H..........H............E.',
      '..H....$.....H..$.....$...E.',
      '..H<<<<<<<<<<H<<<<<<<<<<<.E.',
      '..H..........H............E.',
      '..H..........H............E.',
      '..H...$......H.....$......E.',
      '..H######>>>#H####<<<####.E.',
      '..H..........H............E.',
      '..H..........H............E.',
      '1.H.2........H..m.........E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 'u2-le-monte-charge',
    title: "Le monte-charge",
    world: 'usine',
    // LA LEÇON : on monte sans échelle. Le conduit annule la gravité et porte vers le haut.
    // ⚠️ Un conduit ne s'attrape qu'en MARCHANT DEDANS : il doit descendre jusqu'à la rangée où
    // l'on marche, sinon il est inaccessible. Vérifié au filet AVANT d'écrire la carte — et ce
    // filet ne savait pas monter un conduit, il a fallu le lui apprendre (levels-finissables).
    rows: [
      '..........................E.',
      '..H..$...n.....$...n..$...E.',
      '..H######n#########n#####.E.',
      '..H......n.........n......E.',
      '..H......n.........n......E.',
      '..H....$.n.........n...$..E.',
      '..H######n#########n#####.E.',
      '..H......n.........n......E.',
      '..H......n.........n......E.',
      '..H......n..$......n......E.',
      '..H######n#########n#####.E.',
      '..H......n.........n......E.',
      '..H......n.........n......E.',
      '1.H.2....n......m..n......E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 'u3-les-ressorts',
    title: "Les ressorts",
    world: 'usine',
    // LA LEÇON : rebondir plutôt que grimper. Les dalles sont PERCÉES juste au-dessus de chaque
    // ressort — c'est le rebond qui ouvre l'étage. L'échelle ne dessert que la moitié basse, donc
    // la moitié haute se gagne au ressort ou pas du tout. Ressort simple : 3 cases. Puissant : 5.
    rows: [
      '..........................E.',
      '........$......$......$...E.',
      '...#####.######.######.##.E.',
      '..H.......................E.',
      '..H..$......$......$......E.',
      '..H#####A######A######A##.E.',
      '..H........$.......$......E.',
      '..H.......................E.',
      '..H...$.............$.....E.',
      '..H########^#######^#####.E.',
      '..H.......................E.',
      '..H.......................E.',
      '..H.......................E.',
      '1.H.2..$.........m........E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 'u4-la-chaine',
    title: "La chaîne de montage",
    world: 'usine',
    // LA SYNTHÈSE avant le boss : tapis pour se déplacer, rampes aux deux bouts pour changer
    // d'étage sans échelle, et du sol qui s'effrite dans la dalle haute pour punir l'hésitation.
    rows: [
      '..........................E.',
      '..H...$......H....$....$..E.',
      '..H#####cc###H######cc###.E.',
      '..H..........H............E.',
      '..H..........H............E.',
      '..H..$.......H..$.......$.E.',
      '..H/>>>>>>>>>H>>>>>>>>>>\\.E.',
      '..H..........H............E.',
      '..H..........H............E.',
      '..H......$...H.......$....E.',
      '..H<<<<<<<<<<H<<<<<<<<<<<.E.',
      '..H..........H............E.',
      '..H..........H............E.',
      '1.H.2......m.H......m.....E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 'u5-le-broyeur',
    title: "Le broyeur",
    world: 'usine',
    monkSpeed: 'rapide',
    smartMonks: true,
    // LE BOSS. Aucune mécanique nouvelle : du feu logé dans les creux du tapis, des pics au fond
    // des puits, et deux moines RAPIDES et malins. La difficulté vient de l'enchaînement — c'est
    // la promesse du monde, pas une surprise de plus.
    rows: [
      '..........................E.',
      '..H...$......H.......$....E.',
      '..H##########H###########.E.',
      '..H..........H............E.',
      '..H......$...H....$.......E.',
      '..H>>>>##>>>>H>>>>>##>>>>.E.',
      '..H.......++.H..++........E.',
      '..H..........H............E.',
      '..H..$.......H.........$..E.',
      '..H##########Hcc#########.E.',
      '..H..........H............E.',
      '..H.....""...H....."".....E.',
      '..H..........H............E.',
      '1.H.2.....m.$H........m...E.',
      '############################',
      '============================',
    ],
  },
  // ═══ MONDE DES MIRAGES (31/07) — le sixième monde ═══════════════════════════════════════
  // Mécanique centrale : le TÉLÉPORTEUR. Le moteur l'avait depuis toujours et le parcours ne le
  // faisait jamais jouer — il traînait deux cases sans rôle dans « Les moines fous ». Trois
  // paires (T/t, U/u, V/v), rendues en trois teintes pour qu'on sache où l'on ressort.
  //
  // ⚠️ Le filet de finissabilité ne modélisait PAS les portails : son en-tête le disait, et toute
  // carte dont la route en dépend aurait été déclarée infinissable. Il vient d'apprendre, en
  // calquant `tryTeleport` — entrer sur un portail mène à sa jumelle, sans condition de support.
  {
    slug: 't1-le-passage',
    title: "Le passage",
    world: 'desert',
    // LA LEÇON minimale : un mur de BÉTON coupe la carte en deux. Le béton ne se creuse pas, donc
    // aucune ambiguïté possible sur ce qui ouvre la voie — la seule traversée est le portail.
    rows: [
      '..........................E.',
      '..H..$.......T=t..$.H.....E.',
      '##H###########=#####H####.E.',
      '..H...........=.....H.....E.',
      '..H...........=.....H.....E.',
      '..H.....$.....=.....H.$...E.',
      '##H###########=#####H####.E.',
      '..H...........=.....H.....E.',
      '..H...........=.....H.....E.',
      '..H.$.........=..$..H.....E.',
      '##H###########=#####H####.E.',
      '..H...........=.....H.....E.',
      '..H...........=.....H.....E.',
      '1.H.2.....$...=.....H.$...E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 't2-deux-portes',
    title: "Deux portes",
    world: 'desert',
    // DEUX PAIRES, et elles ne mènent pas au même endroit. On apprend à lire la couleur du
    // portail avant d'entrer : les trois paires sont rendues en trois teintes distinctes.
    rows: [
      '..........................E.',
      '..H..$...........$..H...U.E.',
      '##H#################H####.E.',
      '..H.................H.....E.',
      '..H.................H.....E.',
      '..H$................H$....E.',
      '##H#################H####.E.',
      '..H.................H.....E.',
      '..H.................H.....E.',
      '..Hu...$............H..$..E.',
      '##H#################H####.E.',
      '..H.................H.....E.',
      '..H.................H.....E.',
      '1.H.2.t..$......m...H...T.E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 't3-le-puits',
    title: "Le puits",
    world: 'desert',
    // LA LEÇON : tomber n'est plus une faute. Le puits n'a ni échelle ni prise, son fond porte un
    // portail — la seule sortie est de le prendre. Le portail SAUVE, il ne fait pas que déplacer.
    // Deux pièces flottent dans le puits : on les attrape en tombant, c'est le sujet du niveau.
    rows: [
      '..........................E.',
      '..H..$............$.H.....E.',
      '##H########..#######H####.E.',
      '..H.......=..=......H.....E.',
      '..H.......=$.=......H.....E.',
      '..H.......=..=......H.....E.',
      '..H.......=.$=......H.....E.',
      '##H#######=..=######H####.E.',
      '..H.......=..=......H.....E.',
      '..H.......=T.=......H.....E.',
      '##H#######=##=######H####.E.',
      '..H...$.............H.$...E.',
      '##H#################H####.E.',
      '1.H.2...t.......m...H..$..E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 't4-le-labyrinthe',
    title: "Le labyrinthe",
    world: 'desert',
    // LES TROIS PAIRES ENSEMBLE. Chaque étage a son portail et sa sortie ailleurs : la carte se
    // lit comme un plan, pas comme un couloir.
    rows: [
      '..........................E.',
      '..H...$...........$.H...U.E.',
      '##H#################H####.E.',
      '..H.................H.....E.',
      '..H.................H.....E.',
      '..Hu$...............H.$.V.E.',
      '##H#################H####.E.',
      '..H.................H.....E.',
      '..H.................H.....E.',
      '..Hv....$...........H$....E.',
      '##H#################H####.E.',
      '..H.................H.....E.',
      '..H.................H.....E.',
      '1.H.2.t$....$....m..H...T.E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 't5-le-mirage',
    title: "Le mirage",
    world: 'desert',
    monkSpeed: 'rapide',
    smartMonks: true,
    // LE BOSS. Rien de neuf, sauf que LES MOINES EMPRUNTENT LES MÊMES PORTAILS — `shared/monks.ts`
    // appelle `tryTeleport` comme le coureur. Un poursuivant peut donc surgir de l'autre bout de
    // la carte. Trois paires, du sable, des pics, et deux moines rapides et malins.
    rows: [
      '..........................E.',
      '..H..$.............$H...U.E.',
      '##H######s#######s##H####.E.',
      '..H......s.......s..H.....E.',
      '..H......s.......s..H.....E.',
      '..Hu..$.............H.$...E.',
      '##H#################H####.E.',
      '..H.................H.....E.',
      '..H.$..........$....H...V.E.',
      '##H#################H####.E.',
      '..H.................H.....E.',
      '..H.......""......".H.....E.',
      '..H.................H.....E.',
      '1.Hv2.t.$...m.......H.m.T.E.',
      '############################',
      '============================',
    ],
  },
  // ═══ MONDE DES SERRURES (31/07) — le septième monde ══════════════════════════════════════
  // Mécanique : la PORTE et la CLÉ, dans le moteur depuis les tuiles Phase 3 et jamais jouées.
  //
  // ⚠️ RÈGLE ABSOLUE : une clé n'est JAMAIS enfermée derrière la porte qu'elle ouvre — la carte
  // serait infinissable. Le filet le vérifie depuis aujourd'hui : il parcourt d'abord portes
  // FERMÉES, et ne les rouvre que si une clé est joignable ainsi. Avant, il tenait toute porte
  // pour franchissable « puisque validateLevel garantit qu'une clé existe » — sans jamais vérifier
  // qu'on puisse l'atteindre.
  {
    slug: 's1-la-cle',
    title: "La clé",
    world: 'grotte',
    // LA LEÇON : on ramasse d'abord, on ouvre ensuite. Un mur de béton avec UNE porte, et la clé
    // posée du côté du départ — jamais derrière la porte qu'elle ouvre.
    rows: [
      '..........................E.',
      '..H..$........=...$.H.....E.',
      '##H###########=#####H####.E.',
      '..H...........=.....H.....E.',
      '..H...........=.....H.....E.',
      '..H....k......=.....H.$...E.',
      '##H###########=#####H####.E.',
      '..H...........=.....H.....E.',
      '..H...........=.....H.....E.',
      '..H.$.........=.....H..$..E.',
      '##H###########=#####H####.E.',
      '..H...........=.....H.....E.',
      '..H...........=.....H.....E.',
      '1.H.2....$....D..$..H.....E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 's2-deux-serrures',
    title: "Deux serrures",
    world: 'grotte',
    // DEUX MURS, donc deux clés : `r.keys` est un compteur, la première ouverte est consommée.
    // La seconde clé attend dans la zone du milieu, derrière la première paroi.
    rows: [
      '..........................E.',
      '..H..$...=...H....=.H.$...E.',
      '##H######=###H####=#H####.E.',
      '..H......=...H....=.H.....E.',
      '..H......=...H....=.H.....E.',
      '..Hk.....=..$H....=.H.....E.',
      '##H######=###H####=#H####.E.',
      '..H......=...H....=.H.....E.',
      '..H......=...H....=.H.....E.',
      '..H......=.k.H....D.H.$...E.',
      '##H######=###H####=#H####.E.',
      '..H......=...H....=.H.....E.',
      '..H......=...H....=.H.....E.',
      '1.H.2.$..D...H$...=.H..$..E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 's3-le-trousseau',
    title: "La cellule",
    world: 'grotte',
    // UNE SALLE SCELLÉE dont la porte est au PLAFOND. L'or du fond ne se prend qu'en l'ouvrant
    // puis en descendant par l'échelle intérieure — sans cette échelle ce serait un cul-de-sac :
    // on entrerait sans pouvoir ressortir.
    rows: [
      '..........................E.',
      '..H..$..............H$....E.',
      '##H#################H####.E.',
      '..H.................H.....E.',
      '..H.................H.....E.',
      '..H.k...........$...H.....E.',
      '##H#################H####.E.',
      '..H.................H..$..E.',
      '##H#########D#######H####.E.',
      '..H....=....H....=..H.....E.',
      '..H....=....H....=..H.....E.',
      '..H....=.$..H..$.=..H.....E.',
      '##H####=####H####=##H####.E.',
      '1.H.2.....$.........H.m...E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 's4-le-donjon',
    title: "Le donjon",
    world: 'grotte',
    // Deux murs, deux clés, des moines et du sol qui s'effrite : on ne revient plus
    // tranquillement sur ses pas.
    rows: [
      '..........................E.',
      '..H..$.....=...H$...H=.H..E.',
      '##H####cc##=###H####H=#H#.E.',
      '..H........=...H....H=.H..E.',
      '..H........=...H....H=.H..E.',
      '..Hk.......D...$....H=.H..E.',
      '##H########=###H####H=#H#.E.',
      '..H........=...H....H=.H..E.',
      '..H........=...H....H=.H..E.',
      '..H........=.k.H....H=.$..E.',
      '##H########=###Hcc##H=#H#.E.',
      '..H........=...H....H=.H..E.',
      '..H........=...H....H=.H..E.',
      '1.H.2.m.$..=...m....HD.H..E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 's5-le-gardien',
    title: "Le gardien",
    world: 'grotte',
    monkSpeed: 'rapide',
    smartMonks: true,
    // LE BOSS : un mur, une cellule, de la lave sous une dalle qui cède, deux moines rapides et
    // malins. Deux clés — celle du mur est côté départ, celle de la cellule attend derrière.
    rows: [
      '..........................E.',
      '..Hk..=.............H.$...E.',
      '##H###=#############H####.E.',
      '..H...=.............H.....E.',
      '..H...=..$..........H..$..E.',
      '##H###=#############H####.E.',
      '..H...=...........k.H.....E.',
      '##H###=#######D#####H####.E.',
      '..H.......=...H...=.H.....E.',
      '..H.......=.$.H.$.=.H.....E.',
      '##H#######=#c#H###=#H####.E.',
      '..H.......=.L.....=.H.....E.',
      '..H.................H.....E.',
      '1.Hm2...$...........Hm$...E.',
      '############################',
      '============================',
    ],
  },
  // ═══ LA GRANDE FERME (31/07) — le HUITIÈME et dernier monde ══════════════════════════════
  // Biome `prairie`, le seul que le parcours n'employait pas. Mécanique : le SENS UNIQUE
  // ('»' et '«'), un portillon qu'on ne franchit que dans un sens. Il existait depuis les tuiles
  // du 17/07 et aucun niveau ne s'en servait.
  //
  // ⚠️ Le filet les traversait dans les DEUX sens — ils ne sont pas « solides », donc son test de
  // franchissement les ignorait. Il reprend maintenant la part « contresens » de `blocksFrom`.
  // ⚠️ Un portillon ne bloque que l'HORIZONTAL : la chute le traverse, comme dans le moteur.
  // Un enclos ne se ferme donc jamais par le haut.
  {
    slug: 'f1-le-portillon',
    title: "Le portillon",
    world: 'prairie',
    // LA LEÇON NUE : deux portillons sur la rangée du bas. On file vers la droite, on ne revient
    // pas par là. L'échelle du fond est la seule façon de remonter — c'est elle qui empêche le
    // cul-de-sac, et c'est le vrai sujet du niveau.
    rows: [
      '..........................E.',
      '..H..$............$.H.....E.',
      '##H#################H####.E.',
      '..H.................H.....E.',
      '..H.................H.....E.',
      '..H.....$...........H.$...E.',
      '##H#################H####.E.',
      '..H.................H.....E.',
      '..H.................H.....E.',
      '..H.$...........$...H.....E.',
      '##H#################H####.E.',
      '..H.................H.....E.',
      '..H.................H.....E.',
      '1.H.2...»..$...»....H..$..E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 'f2-le-detour',
    title: "Le détour",
    world: 'prairie',
    // Chaque étage tourne dans un sens : à droite en bas, à gauche au milieu, à droite en haut.
    // On ne revient jamais sur ses pas — il faut prévoir l'ORDRE du ramassage.
    rows: [
      '..........................E.',
      '..H...$.....».......H$....E.',
      '##H#################H####.E.',
      '..H.................H.....E.',
      '..H.................H.....E.',
      '..H.$.........«.....H..$..E.',
      '##H#################H####.E.',
      '..H.................H.....E.',
      '..H.................H.....E.',
      '..H....».$.........$H.....E.',
      '##H#################H####.E.',
      '..H.................H.....E.',
      '..H.................H.....E.',
      '1.H.2...$....»......H.$...E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 'f3-l-enclos',
    title: "L'enclos",
    world: 'prairie',
    // Deux portillons opposés forment un couloir qu'on traverse sans retour. La sortie est un
    // trou dans le plancher : on tombe à l'étage du dessous, où l'échelle attend.
    // ⚠️ Un portillon ne bloque que l'HORIZONTAL — la chute le traverse, comme dans le moteur.
    rows: [
      '..........................E.',
      '..H..$..............H.$...E.',
      '##H#################H####.E.',
      '..H.................H.....E.',
      '..H.................H.....E.',
      '..H.....»...$....«..H.....E.',
      '##H##########.######H####.E.',
      '..H.................H.....E.',
      '..H.................H.....E.',
      '..H...$............$H.....E.',
      '##H#################H####.E.',
      '..H.................H.....E.',
      '..H.................H.....E.',
      '1.H.2.....$.....$...H..m..E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 'f4-le-troupeau',
    title: "Le troupeau",
    world: 'prairie',
    // Les MOINES empruntent les portillons comme le coureur (`blocksFrom` vaut pour tout le
    // monde). Un portillon n'est donc pas un abri : c'est une contrainte partagée.
    rows: [
      '..........................E.',
      '..H..$.....»........$.....E.',
      '##H#####cc##########H####.E.',
      '..H.................H.....E.',
      '..H.................H.....E.',
      '..H$...........$..«.H..$..E.',
      '##H#################H####.E.',
      '..H.................H.....E.',
      '..H.................H.....E.',
      '..H....$....».......H$....E.',
      '##H#############cc##H####.E.',
      '..H.................H.....E.',
      '..H.................H.....E.',
      '1.H.2.m..$....m.....H.m...E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 'f5-le-grand-pre',
    title: "Le grand pré",
    world: 'prairie',
    monkSpeed: 'rapide',
    smartMonks: true,
    // LE DERNIER NIVEAU DU PARCOURS. La synthèse : portillons, sol qui s'effrite, une porte à
    // clé, un ressort encastré pour rattraper l'étage perdu, deux moines rapides et malins.
    // Aucune mécanique nouvelle — c'est la promesse tenue du parcours : à la fin, on sait tout.
    rows: [
      '..........................E.',
      '..H.k........»......H$....E.',
      '##H######=##########H####.E.',
      '..H......=..........H.....E.',
      '..H...$..=.......$..H.....E.',
      '##H######D##########H####.E.',
      '..H......=..........H.....E.',
      '..H......=.....$....H..$..E.',
      '##H######=##cc######H####.E.',
      '..H......=........«.H.....E.',
      '..H..$...=..........H.$...E.',
      '##H######=#####A####H####.E.',
      '..H......=..........H.....E.',
      '1.H.2..$.D..m....$..H..m..E.',
      '############################',
      '============================',
    ],
  },
  {
    // Arène duel à ciel ouvert, 3 étages creusables, symétrie miroir stricte.
    // Échelle de sortie CENTRALE (cols 13-14, plantée sur l'étage haut) :
    // équidistante des deux spawns (égalité versus et sommet coop).
    // Paire de tazers au sol (cols 5/22), en miroir : chaque joueur a le sien,
    // le duel reste équitable.
    // Départ J3 (mode 3 joueurs) : centre-bas (col 12), sur le sol plein, à la
    // même hauteur que J1/J2, entre les deux (équité de rampe vers la sortie).
    slug: '06-arene-face-a-face',
    title: 'Arène : face à face',
    world: 'prairie',
    rows: [
      '.............EE.............',
      '.............EE.............',
      '.............EE.............',
      '.............EE.............',
      '..........$..EE..$..........',
      '........#H########H#........',
      '.........H........H.........',
      '.........H........H.........',
      '......$..H........H..$......',
      '..#H####################H#..',
      '...H....................H...',
      '...H....................H...',
      '...H....................H...',
      '.1.H.z......3$$.......z.H.2.',
      '############################',
      '============================',
    ],
  },
  {
    // Arène duel souterraine : 5 galeries-tunnels percées dans la masse de
    // briques, échelles en zigzag miroir (cols 4/23, 9/18, 13/14, 1/26),
    // paire de téléporteurs en coins opposés (T haut-gauche, t bas-droit).
    // Sortie centrale cols 13-14 en surface. Paire de tazers au sol (cols 5/22),
    // en miroir, à côté de chaque spawn : duel équitable.
    // Départ J3 (mode 3 joueurs) : centre-bas (col 12), dans la galerie du bas,
    // sur le sol plein, à la même hauteur que J1/J2 et entre les deux.
    slug: '07-arene-souterraine',
    title: 'Arène : souterraine',
    world: 'grotte',
    rows: [
      '.............EE.............',
      'TH...........EE...........H.',
      '#H########################H#',
      '#H########################H#',
      '.H........$..HH..$........H.',
      '#############HH#############',
      '#############HH#############',
      '.........H.$.HH.$.H.........',
      '#########H########H#########',
      '#########H########H#########',
      '....H.$..H........H..$.H....',
      '####H##################H####',
      '####H##################H####',
      '....Hz1.....3$$......2zH...t',
      '############################',
      '============================',
    ],
  },
  {
    // Grande carte de DÉMONSTRATION (Lot E) : 56×32, la caméra suit le joueur. Tour à étages,
    // or réparti jusqu'aux extrémités (force le défilement horizontal), échelles + cordes,
    // moines, sortie centrale. Catalogue seul (pas de .txt) ; départ J2 -> aussi jouable en duo.
    slug: '08-grande-carte',
    title: 'Grande carte (démo caméra)',
    world: 'usine',
    // ⚠️ MEUTE ALLUMÉE le 26/07 (chantier « des moines qui chassent vraiment »). Mesuré en
    // production : 0,0 à 0,9 mort par joueur sur les sept premiers niveaux — on traversait le
    // parcours sans mourir. Le comportement malin (anticipation deux cases devant, ligne de
    // vue, mémoire de la dernière position, patrouille) et les bulles ! / ? / z EXISTAIENT
    // déjà : le drapeau était simplement faux sur dix niveaux sur onze.
    smartMonks: true,
    rows: [
      '=.............................................E........=',
      '=.1.........................m.................E......2.=',
      '=...............------------------------......E........=',
      '=..$................$..............$..........E......$.=',
      '=#######.#####H############.############H#####E########=',
      '=.............H.........................H.....E........=',
      '=.............H.........................H.....E........=',
      '=..$..........H.....$..............$....H.....E......$.=',
      '=####H##.##################.##################E###H####=',
      '=....H........................................E...H....=',
      '=....H........................................E...H....=',
      '=..$.H..............$..............$..........E...H..$.=',
      '=#######.#####H#####m######.############H#####E########=',
      '=.............H.........................H.....E........=',
      '=.............H.........................H.....E........=',
      '=..$..........H.....$..............$....H.....E......$.=',
      '=####H##.##################.##################E###H####=',
      '=....H........................................E...H....=',
      '=....H........................................E...H....=',
      '=..$.H..............$..............$..........E...H..$.=',
      '=#######.#####H############.############m#####E########=',
      '=.............H.........................H.....E........=',
      '=.............H.........................H.....E........=',
      '=..$..........H.....$..............$....H.....E......$.=',
      '=####H##.##################.##################E###H####=',
      '=....H........................................E...H....=',
      '=....H........................................E...H....=',
      '=..$.H..............$.......E......$..........E...H..$.=',
      '=#######.#####H############.E###########H#####E########=',
      '=.............H.............E...........H.....E........=',
      '=..b..........H.............E...........H.....E.....z..=',
      '==============H=========================H===============',
    ],
  },
  // --- CARTES MOYENNES 42x24 (16/07 nuit, demande Claude-Alain « 3 map fun à jouer de taille
  // moyenne ») : une par nouveau monde + un parcours de machines. Chacune tient dans la caméra du
  // Lot E (viewport 28x16 -> ça défile), a un départ J1/J2/J3, et est vérifiée finissable (BFS
  // conservateur : marche/échelle/corde/chute/nage) par tests/levels-catalog.test.ts.
  {
    slug: 'a1-premiere-apnee',
    title: 'Première apnée',
    world: 'abysses',
    rows: [
      '..........................E.',
      '..........................E.',
      '..........................E.',
      '.......$..........$.......E.',
      '....############H#########E.',
      '1.2.............H.........E.',
      '#####H##########H#########E.',
      '.....H....................E.',
      '.....H###############.....E.',
      '.....H~~~~~~~~~~~~~~#.....E.',
      '.....H~~~~~~~~~~~~~~#.....E.',
      '.....H~~~~$~~~~~$~~~#.....E.',
      '.....H~~~~~~~~~~~~~~#.....E.',
      '.....H~~~~~:~~~~~~~~#.....E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 'a2-de-bulle-en-bulle',
    title: 'De bulle en bulle',
    world: 'abysses',
    rows: [
      '..........................E.',
      '..........................E.',
      '1.2.......................E.',
      '####H#####################E.',
      '....H.....................E.',
      '....H##########.#######...E.',
      '....H~~~~~~~~~~~~~~~~~~#..E.',
      '....H~~~$~~~~~~#~~~~$~~#..E.',
      '....H~~~~~~~~~~#~~~~~~~#..E.',
      '....H~~~:~~~~~~#~~:~~~~#..E.',
      '....H###################..E.',
      '....H.....................E.',
      '....H.....................E.',
      '....H.....................E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 'a3-le-siphon',
    title: 'Le siphon',
    world: 'abysses',
    rows: [
      '..........................E.',
      '1.2.......................E.',
      '#####.####################E.',
      '....#.#...................E.',
      '....#~#...................E.',
      '....#~#....$..............E.',
      '....#~#...######H#########E.',
      '....#~#.........H.........E.',
      '....#~#.....####H#########E.',
      '....#~#.........H.........E.',
      '....#$#.........H.........E.',
      '....#~#.........H.........E.',
      '....#:..........H.........E.',
      '................H.........E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 'a4-plus-profond',
    title: 'Plus profond',
    world: 'abysses',
    rows: [
      '..........................E.',
      '..........................E.',
      '1.2.....m.................E.',
      '#####H###################.E.',
      '.....H....................E.',
      '.####H###############.....E.',
      '.....H...............#....E.',
      '.####H###############....#E.',
      '.....H~~~~~~~~~~~~~~~#....E.',
      '.....H~~~$~~~~~~~~$~~#....E.',
      '.....H~~~~~~~~~~~~~~~#....E.',
      '.....H~~:~~~~~~~~~:~~#....E.',
      '.....H~~~~~~~$~~~~~~~#....E.',
      '.....H###############.....E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: '09-epave-engloutie',
    title: "L'épave engloutie",
    world: 'abysses',
    // ⚠️ MEUTE ALLUMÉE le 26/07 (chantier « des moines qui chassent vraiment »). Mesuré en
    // production : 0,0 à 0,9 mort par joueur sur les sept premiers niveaux — on traversait le
    // parcours sans mourir. Le comportement malin (anticipation deux cases devant, ligne de
    // vue, mémoire de la dernière position, patrouille) et les bulles ! / ? / z EXISTAIENT
    // déjà : le drapeau était simplement faux sur dix niveaux sur onze.
    smartMonks: true,
    decor: '..........................................\n..........................................\n..........................................\n..........................................\n..........................................\n.....C.........................C..........\n..........................................\n...................?......................\n..........................................\n..........................................\n..........................?...............\n..........................................\n..........................................\n.....[...........................[........\n..........................................\n..........................................\n..........................................\n..........................................\n..........................................\n..........................................\n...............K........0.................\n...[........[....[...[.......[......[.....\n..........................................\n..........................................',
    rows: [
      '=......................................E.=',
      '=......................................E.=',
      '=......................................E.=',
      '=......................................E.=',
      '=......................................E.=',
      '=.123._.$H..................H....$.Hz.$E.=',
      '=########H..................H######H###E#=',
      '=........H..H...$...m...$...H......H...E.=',
      '=.........##H################......H...E.=',
      '=...........H......................H...E.=',
      '=..H.$...S..H$..............$......H...E.=',
      '=.#H###############.....###############E.=',
      '=..H................$.H_$..............E.=',
      '=..H................##H##..............E.=',
      '=.~H~~~~~~~~~~~~~~~~~~H~~~~~~~~~~~~~~#.E.=',
      '=.~H~~~~~~~~~~~~~~$~~~H~~~~~~~~~~~~~~#.E.=',
      '=.~H$~~~~~~~~~~~:~~~~~H~~~~~~~~:~~$~~#.E.=',
      '=.~H~~~~~~============H=====~~~~~~~~~#.E.=',
      '=.~~~~~~~~=..#######..H..j.=~~~~~~~~~#.E.=',
      '=.~~~~:~~~=...........H####=~$~~~~~~~#.E.=',
      '=.~~~~~~~~~...$.......Hk$..=~~~~~~~~~D.E.=',
      '=.~~~~~""~==================~~"~~:~"~####=',
      '=########################################=',
      '==========================================',
    ],
  },
  {
    slug: 'm1-coulees',
    title: 'Coulées',
    world: 'manoir',
    rows: [
      '..........................E.',
      '..........................E.',
      '1.2.......................E.',
      '########H...#########...##E.',
      '........H............H....E.',
      '........H............H....E.',
      '........H............H....E.',
      '........H...$.....$..H.$..E.',
      '........H############H####E.',
      '........H...........#.....E.',
      '........H...........#.....E.',
      '........H...........#.....E.',
      '........H...........#.....E.',
      'LLLLLLLL...$..LLLLLL......E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 'm2-le-marteau',
    title: 'Le marteau',
    world: 'manoir',
    monkPowers: true,
    smartMonks: true,
    rows: [
      '..........................E.',
      '..........................E.',
      '1.2...........m...........E.',
      '######H############H######E.',
      '......H............H......E.',
      '......H............H......E.',
      '......H............H......E.',
      '...$..H....$.......H..$...E.',
      '############H#############E.',
      '............H.............E.',
      '............H.............E.',
      '............H.............E.',
      '............H.............E.',
      '.....$......H.......$.....E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 'm3-meche-courte',
    title: 'Mèche courte',
    world: 'manoir',
    rows: [
      '..........................E.',
      '..........................E.',
      '1.2.......................E.',
      '####H#################H###E.',
      '....H.................H...E.',
      '....H.................H...E.',
      '....H.....$.....$.....H...E.',
      '#############H############E.',
      '.............H............E.',
      '.............H............E.',
      '.............H............E.',
      '.............H......$.....E.',
      '####.++++++++H############E.',
      '.......$...........$......E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 'm4-face-a-face',
    title: 'Face à face',
    world: 'manoir',
    monkPowers: true,
    smartMonks: true,
    rows: [
      '..........................E.',
      '..........................E.',
      '1.2.......m.........m.....E.',
      '#####H##########H#########E.',
      '.....H..........H.........E.',
      '.....H..........H.........E.',
      '.....H..........H.........E.',
      '.....H..$.......H....$....E.',
      '##H############.H#########E.',
      '..H...#.........H.........E.',
      '..H...#.........H.........E.',
      '..H...#.........H.........E.',
      '..H...#.........H.........E.',
      '..H$..#LLLLLL...H..$......E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: '10-manoir-hante',
    title: "Le manoir hanté",
    world: 'manoir',
    monkSpeed: "lent",
    smartMonks: true, // 17/07 (lot « la meute vit ») : patrouilles + repérages -> les ÉMOTIONS (!/?/z) vivent ici ; lents = pression familiale correcte
    decor: '..........................................\n..........................................\n..........................................\n..........................................\n..........................................\n..........................................\n......W................C...........|...W..\n..........................................\n..........................................\n..........................................\n..........................................\n..|......h..i.................i...........\n..........................................\n..........................................\n..........................................\n..........................................\n.......f...........K..................f...\n..........................................\n..........................................\n..........................................\n..........................................\n..o..|.........K.................|........\n..........................................\n..........................................',
    rows: [
      '=....................E...................=',
      '=....................E...................=',
      '=....................E...................=',
      '=..........H.........E...................=',
      '=..........H.........E...................=',
      '=..........H.........E...................=',
      '=...$......H......_..E....µ...$......$...=',
      '=##########H###ccc#######################=',
      '=.....G....H......................H...t..=',
      '=..........H......................H......=',
      '=..........H......................H......=',
      '=..$.......H..---$....D...$.M....$H.j....=',
      '=#############...#################H######=',
      '=.......H........---....+.........H......=',
      '=.......H.........................H......=',
      '=.......H.........................H......=',
      '=.123._.H.....$.....S.......$.....Hz...$.=',
      '=#######H##ccc#########################x#=',
      '=.......H................................=',
      '=.......H................................=',
      '=.......H...+.......####.................=',
      '=..$....H.......$....""..`$...k.....$.T..=',
      '=########################################=',
      '==========================================',
    ],
  },
  {
    slug: '11-grande-machine',
    title: "La grande machine",
    world: 'usine',
    monkSpeed: "rapide",
    // ⚠️ MEUTE ALLUMÉE le 26/07 (chantier « des moines qui chassent vraiment »). Mesuré en
    // production : 0,0 à 0,9 mort par joueur sur les sept premiers niveaux — on traversait le
    // parcours sans mourir. Le comportement malin (anticipation deux cases devant, ligne de
    // vue, mémoire de la dernière position, patrouille) et les bulles ! / ? / z EXISTAIENT
    // déjà : le drapeau était simplement faux sur dix niveaux sur onze.
    smartMonks: true,
    decor: '..........................................\n..........................................\n..........................................\n..........................................\n..........................................\n...............9..........Y...............\n..........................................\n..........................................\n..........................................\n..........................................\n........h.Y.......................Y.......\n..........................................\n..........................................\n..........................................\n..........................................\n...........Y...................Y..........\n..........................................\n..........................................\n..........................................\n..........................................\n..o...................................o...\n........Y....................Y............\n..........................................\n..........................................',
    rows: [
      '=................E.......................=',
      '=................E.......................=',
      '=................E.....................H.=',
      '=........HF.$....E.....................H.=',
      '=........H###\\...E.....................H.=',
      '=...$....H.m.....E.._..$..........j...$H.=',
      '=###########################IIII#######H#=',
      '=..H...................................H.=',
      '=..H...................................H.=',
      '=..H...................................H.=',
      '=..H...$.......-------...b..$..m....$..H.=',
      '=##H###########.......###################=',
      '=..H..n................................H.=',
      '=..H..n................................H.=',
      '=..H..n.................$$.............H.=',
      '=..H.$n..z.....$.....m..#####\\$.....$..H.=',
      '=###########<<<<<<<<<###ccc############H#=',
      '=................................$.....H.=',
      '=.........................$............H.=',
      '=......................................H.=',
      '=..........$...........................H.=',
      '=.123._.../#..$...$...$...S.$.""...X.R.H.=',
      '=###########>>>>>>>>>>>>>#^######A#######=',
      '==========================================',
    ],
  },
  {
    slug: 'p1-le-piege-complet',
    title: 'Le piège complet',
    world: 'classique',
    // CARTE DE TEST (31/07, demande Claude-Alain « une carte de test avec un piège à moine
    // complet »). Elle met bout à bout ce qui a été ajouté ces deux jours, et rien d'autre.
    //
    //   LA NASSE (droite). Une GRILLE (¦ col 22) commandée par un INTERRUPTEUR (° col 20), et une
    //   chambre sans issue derrière (cols 23-27). Marcher sur l'interrupteur ouvre la grille ; en
    //   sortir la referme 3 s plus tard. Le piège complet : ouvrir, aller poser l'APPÂT (æ col 6)
    //   AU FOND de la chambre, ressortir, se remettre sur l'interrupteur, attendre que le moine
    //   file vers l'appât — et lâcher. La grille retombe DERRIÈRE lui : il est enfermé.
    //   L'interrupteur est volontairement à DEUX cases de la grille : depuis là, l'aller-retour
    //   pour poser l'appât tient largement dans les 3 s de grâce. La CAISSE MÉTAL (X col 17) sert
    //   à tenir la grille ouverte sans y rester, pour ceux qui préfèrent prendre leur temps.
    //   ⚠️ Faire retomber la grille PILE sur le moine (donc l'écraser) est possible mais demande
    //   le tick près : ses 3 s d'appât et les 3 s de grâce ont exactement la même durée.
    //
    //   L'EMMUREMENT (gauche). Creuser le sol, laisser un moine y tomber, puis MAINTENIR la touche
    //   creuser vers le trou : quatre coups de pioche le comblent et le moine y reste VIVANT,
    //   réduit à deux gros yeux dans la brique. Recreuser la même brique le libère.
    //
    // Deux moines, un par piège. Sol entièrement creusable : le trou se pose où l'on veut.
    // ⚠️ La chambre derrière la grille est VIDE et l'échelle de sortie est à GAUCHE, hors du
    // circuit : le filet de finissabilité ne modélise pas les barrières, une sortie enfermée
    // derrière elle serait donc signalée injoignable à tort. Le piège reste facultatif — c'est
    // une carte d'essai, pas une épreuve.
    rows: [
      'E...........................',
      'E...........................',
      'E...........................',
      'E...........................',
      'E...........................',
      'E...........................',
      'E...........................',
      'E...........................',
      'E...........................',
      'E...........................',
      'E...........................',
      'E...........................',
      'E...........................',
      'E12.$.æ..m.$..m..X..°.¦.....',
      '############################',
      '============================',
    ],
  },
  // ═══ LA FOIRE AUX FARCES (13/08) — la neuvième île, et la première bâtie sur les OBJETS ═══
  // Les neuf objets du versus (06/08) n'existaient que dans les arènes de duel. Six d'entre eux
  // fonctionnent pourtant très bien SEUL, contre des moines — mesuré objet par objet avant
  // d'écrire la moindre grille : maillet (le moine part en vol, la porte cède), déguisement
  // (immunité au contact), bandelette (elle enroule un MOINE), crapaud, géant, couronne.
  // Les trois autres restent au duel : la meute ne viserait que soi, le fantôme ne trompe pas
  // les moines, et la patate tue son porteur faute de voisin à qui la refiler.
  // ⚠️ Biome `prairie`, déjà porté par « La grande ferme » : c'est la PREMIÈRE île à réutiliser
  // un biome. Les huit clés de `WorldKey` sont prises, et une neuvième exigerait une palette de
  // tuiles régénérée dans la feuille de sprites. Après la nuit d'orage du manoir, le plein jour
  // d'une prairie est de toute façon le contraste le plus franc du parcours.
  {
    slug: 'x1-la-masse',
    title: 'La masse',
    world: 'prairie',
    // LA LEÇON : la pioche s'échange contre un MAILLET, et le marché est brutal — on ne creuse
    // plus du tout, mais on envoie valser les moines et on DÉFONCE une porte fermée sans clé.
    // ⚠️ La clé existe quand même, en haut de la grande échelle : le maillet est le raccourci,
    // jamais l'unique route. Le filet de finissabilité ne sait pas défoncer une porte.
    rows: [
      '..................=.......E.',
      '..H......$........=.......E.',
      '..H#######........=.......E.',
      '..H...............=.......E.',
      '..H....k..........=.......E.',
      '..H#######........=.......E.',
      '..H...............=.......E.',
      '..H...............=.......E.',
      '..H...............=.......E.',
      '..H...............=..$....E.',
      '..H...............=.H#####E.',
      '..H....$..........=.H.....E.',
      '..H#####..........=.H.....E.',
      '1.H.2.œ.....m.....D.H$....E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 'x2-le-faux-moine',
    title: 'Le faux moine',
    world: 'prairie',
    // LA LEÇON : sous la robe, ils ne te touchent plus. Le couloir du bas est gardé par trois
    // moines et l'or du fond s'y ramasse sans une égratignure — pendant dix secondes seulement.
    // Mesuré avant d'écrire la carte : déguisé, on survit à 250 ticks de contact ; sans, on meurt
    // au 71e. C'est bien une protection, pas une invisibilité : ils viennent quand même.
    rows: [
      '..........................E.',
      '..H.......................E.',
      '..H..$.....$......$.......E.',
      '..H#######################E.',
      '..H.......................E.',
      '..H..ø....................E.',
      '..H#######################E.',
      '..H.......................E.',
      '..H.......................E.',
      '..H.......................E.',
      '..H.......................E.',
      '..H.......................E.',
      '..H.......................E.',
      '1.H.2....m.....m.....m$...E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 'x3-le-drapeau',
    title: 'Le drapeau',
    world: 'prairie',
    // LA LEÇON : le drapeau baladeur te change en crapaud. Tu bondis alors tout seul par-dessus
    // les trous d'une case, mais tu ne creuses plus et tu ne ramasses plus d'objet ; la touche
    // creuser te rend ta peau. Les plateformes sont donc trouées d'une case, régulièrement.
    // ⚠️ Les échelles gardent une route entière pour qui n'attrape jamais le drapeau.
    rows: [
      '..........................E.',
      '..H--------------------...E.',
      '..H..$....$....$....$.....E.',
      '..H##.###.###.###.####....E.',
      '..H.......................E.',
      '..H....ð..................E.',
      '..H--------------------...E.',
      '..H.$....$....$....$......E.',
      '..H#.###.###.###.###......E.',
      '..H.......................E.',
      '..H.......................E.',
      '..H...$...................E.',
      '..H#####..................E.',
      '1.H.2..........m..........E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 'x4-la-couronne',
    title: 'La couronne',
    world: 'prairie',
    // LA LEÇON : elle fabrique une pièce par seconde et NE SE RETIRE PAS.
    // ⚠️ MESURÉ, ET CONTRE MON INTUITION : en SOLO, la hiérarchie « les moines visent le couronné
    // en priorité » ne change strictement RIEN — il n'y a qu'un coureur, ils te chassaient déjà.
    // Distance du moine le plus proche après cinq secondes : trois cases, couronné ou non.
    // La contrepartie ne peut donc pas venir de l'objet, elle vient de la CARTE : la couronne est
    // posée à l'étage du bas, entre les deux moines. On l'achète en descendant chez eux.
    // ⚠️ Chaque pièce créée s'ajoute AUSSI au total de la carte : le compteur reste honnête, et
    // couronné on ne finit pas la partie plus vite — on la finit plus riche.
    rows: [
      '..........................E.',
      '..H.......................E.',
      '..H.$...$...$...$...$.....E.',
      '..H#######################E.',
      '..H.......................E.',
      '..H.......................E.',
      '..H.......................E.',
      '..H.......................E.',
      '..H#######################E.',
      '..H.......................E.',
      '..H.......................E.',
      '..H..m......Ħ..m..........E.',
      '..H#######################E.',
      '1.H.2.$...............$...E.',
      '############################',
      '============================',
    ],
  },
  {
    slug: 'x5-la-grande-farce',
    title: 'La grande farce',
    world: 'prairie',
    // LE BOSS DE L'ÎLE : quatre moines, deux réponses. La bandelette les enroule là où on l'a
    // tendue (elle épargne son poseur 1,5 s, le temps de s'écarter) ; la potion de géant les
    // écrase en marchant. Aucune des deux n'est obligatoire — mais sans elles, c'est très long.
    rows: [
      '..........................E.',
      '..H.......................E.',
      '..H..$.........$..........E.',
      '..H#######################E.',
      '..H.......................E.',
      '..H......þ................E.',
      '..H#######################E.',
      '..H.......................E.',
      '..H..............Ł........E.',
      '..H#######################E.',
      '..H.......................E.',
      '..H..$....................E.',
      '..H####...................E.',
      '1.H.2..m....m....m....m..$E.',
      '############################',
      '============================',
    ],
  },
  // ══════════════════ LES CINQ ARÈNES DU VERSUS (06/08) ══════════════════
  // Demande de Claude-Alain : « 5 maps spécifiques pour le pvp, varié, avec plusieurs mécaniques
  // différentes ». Une mécanique DOMINANTE par arène plutôt qu'un peu de tout partout — on doit
  // pouvoir dire en une phrase à quoi ressemble une partie sur chacune.
  //
  // ⚠️ SYMÉTRIE MIROIR STRICTE, et c'est le point qui compte le plus pour du duel. Chaque arène
  // est construite par reflet de sa moitié gauche (les chars orientés s'inversent : < > ( ) { }
  // / \\ » «), donc les deux camps ont EXACTEMENT les mêmes appuis, le même or et les mêmes
  // objets, à la case près. Seul le départ du 3e joueur est décalé — comme dans les deux arènes
  // d'origine : à trois, la symétrie parfaite n'existe pas.
  //
  // Hors PARCOURS (cf. ARENA_SLUGS) : le solo ne les compte pas comme des étapes.

  {
    // L'EAU. Un bassin central que l'or le plus riche tapisse au fond : plus tu descends, plus tu
    // gagnes, et moins il te reste de souffle. Deux piranhas patrouillent, deux sources de bulles
    // permettent de tenir. Les échelles bordent le bassin — c'est par elles qu'on entre et qu'on sort.
    slug: 'v1-le-grand-bain',
    title: 'Arène : le grand bain',
    world: 'abysses',
    rows: [
      '.............EE.............',
      '.............EE.............',
      '.............EE.............',
      '..H.........3EE..........H..',
      '..H...$..H########H..$...H..',
      '..H####..H........H..####H..',
      '..H......H........H......H..',
      '..H..$...H........H...$..H..',
      '..H####..H........H..####H..',
      '..H......H~~~~~~~~H......H..',
      '..H..$...H~~~~~~~~H...$..H..',
      '..H####..H~,~~~~,~H..####H..',
      '..H......H~~~~~~~~H......H..',
      '.1Hz.....H~~$~~$~~H.....zH2.',
      '##########~~~::~~~##########',
      '============================',
    ],
  },
  {
    // LES TRAMPOLINES, et RIEN d'autre pour monter : pas une seule échelle. Chaque palier est à trois
    // cases du précédent — la hauteur exacte d'un rebond — et volontairement décalé, pour qu'il faille
    // piloter sa dérive en l'air. Rater le palier, c'est retomber tout en bas.
    slug: 'v2-la-tour-des-ressorts',
    title: 'Arène : la tour des ressorts',
    world: 'usine',
    rows: [
      '.............EE.............',
      '.............EE.............',
      '.............EE.............',
      '.............EE.............',
      '...$.........EE.........$...',
      '....####################....',
      '............................',
      '.....$................$.....',
      '.##^##................##^##.',
      '............................',
      '.......$............$.......',
      '.....#^###........###^#.....',
      '............................',
      '.1........z..3...z........2.',
      '##########^######^##########',
      '============================',
    ],
  },
  {
    // LES TAPIS. Deux conduits montants font toute la verticalité : on y entre en bas, on en sort à
    // l'étage qu'on veut, à condition qu'un palier soit là. Les tapis de la passerelle convergent vers
    // le centre : en haut, le décor pousse tout le monde vers la sortie.
    slug: 'v3-l-usine',
    title: 'Arène : l\'usine',
    world: 'usine',
    rows: [
      '.............EE.............',
      '.............EE.............',
      '.............EE.............',
      '.............EE.............',
      '....#########EE#########....',
      '....n....$...EE...$....n....',
      '....n>>>>>>>>##<<<<<<<<n....',
      '....n..................n....',
      '....n..$............$..n....',
      '....n#####........#####n....',
      '....n..................n....',
      '....n..$............$..n....',
      '....n#####........#####n....',
      '.1..n.......z3.z.......n..2.',
      '############################',
      '============================',
    ],
  },
  {
    // LES PIÈGES. Une nappe de lave au fond, deux fausses briques en BOUT de plateforme (la course
    // qui ne s'arrête pas finit dessus) et les pics juste en dessous, pour que la chute se comprenne
    // d'un coup d'œil. Un rocher par camp, à pousser sur l'adversaire — ou dans la lave.
    slug: 'v4-la-fosse',
    title: 'Arène : la fosse',
    world: 'manoir',
    rows: [
      '.............EE.............',
      '.............EE.............',
      '.............EE.............',
      '..H......$..3EE...$......H..',
      '..H######################H..',
      '..H......................H..',
      '..H..$................$..H..',
      '..H#####xx........xx#####H..',
      '..H.....""........"".....H..',
      '..H..$.]............].$..H..',
      '..H#####............#####H..',
      '..H......................H..',
      '..H......LLLLLLLLLL......H..',
      '.1H.z....##########....z.H2.',
      '############################',
      '============================',
    ],
  },
  {
    // LES NEUF OBJETS DU VERSUS, un jeu complet par camp : maillet, patate chaude, fantôme, meute,
    // drapeau-crapaud, potion de géant, déguisement, bandelette — et la couronne, enfermée dans une
    // alcôve que seule la barrière garde. Le bouton qui l'ouvre est en face : presser sert forcément
    // à quelqu'un, et pas toujours à soi.
    slug: 'v5-l-arsenal',
    title: 'Arène : l\'arsenal',
    world: 'prairie',
    rows: [
      '.............EE.............',
      '.............EE.............',
      '.............EE.............',
      '..H...œ..$..3EE...$..œ...H..',
      '..H######################H..',
      '..H......................H..',
      '..H..Ŧ.ĸ............ĸ.Ŧ..H..',
      '..H#####............#####H..',
      '..H......................H..',
      '..H..$.ð............ð.$..H..',
      '..H#####............#####H..',
      '..H......°Ŋ.þ..þ.Ŋ°......H..',
      '..H.....############.....H..',
      '.1H.z.ø.Ł.$Ħ.¦¦.Ħ$.Ł.ø.z.H2.',
      '############################',
      '============================',
    ],
  },
  // ══════════════ LES ARÈNES DE COMBAT (« ring-out », 06/08) ══════════════
  // Demande de Claude-Alain : « de nouvelles map plus de type arène et moins rush les pièces d'or.
  // Bump avec marteau à l'extérieur de la map ».
  //
  // ⚠️ LE VIDE EST DANS LA GRILLE, PAS DEHORS. Hors grille, `tileAt` renvoie du béton — un mur
  // virtuel volontaire (« on ne sort jamais »), contre lequel un vol de maillet s'arrête net. Le
  // précipice se creuse donc À L'INTÉRIEUR : les dernières rangées restent VIDES, et la règle
  // « tomber jusqu'à la dernière rangée tue » (12/07) fait le reste. Zéro changement de moteur.
  //
  // ⚠️ ET IL Y A PLUS D'OR QU'AILLEURS, ce qui semble contredire « moins rush l'or ». C'est
  // l'inverse : une partie se termine quand TOUT l'or est ramassé (`checkWin`). Deux pièces sur
  // une arène de combat, et le premier qui les prend clôt la partie — la course serait plus
  // brutale encore. Beaucoup d'or, posé aux endroits exposés, plus la pièce que chaque mort
  // relâche en versus : voilà ce qui fait durer un combat au lieu d'une course.
  //
  // Pas un seul moine : au-dessus d'un vide, ils tomberaient, mourraient et renaîtraient sans fin.
  {
    // LE RING-OUT. Un plateau large SUSPENDU AU-DESSUS DU VIDE : sortir du bord, c'est tomber, et
    // tomber tue. Le maillet devient une arme d'éjection — trois cases de vol suffisent à faire
    // passer quelqu'un par-dessus la rambarde. L'or est nombreux et posé sur les bords : le
    // ramasser, c'est s'exposer.
    slug: 'r1-le-ring',
    title: 'Ring : le plateau',
    world: 'desert',
    rows: [
      '.............EE.............',
      '.............EE.............',
      '.............EE.............',
      '.........H$..EE..$H.........',
      '.........H########H.........',
      '.........H........H.........',
      '.........H........H.........',
      '...$.œ.$.H........H.$.œ.$...',
      '...######H........H######...',
      '.........H........H.........',
      '.........H........H.........',
      '.1.$z.$..H.$.3..$.H..$.z$.2.',
      '.##########################.',
      '............................',
      '............................',
      '............................',
    ],
  },
  {
    // DEUX ÎLES ET UN GOUFFRE. La corde est le seul passage à pied d'une rive à l'autre, et l'on y
    // est terriblement exposé ; le ressort permet de remonter par en dessous. On se croise
    // forcément au milieu, au-dessus du vide — ce qui est exactement le but.
    slug: 'r2-les-deux-iles',
    title: 'Ring : les deux îles',
    world: 'neige',
    rows: [
      '.............EE.............',
      '.............EE.............',
      '.............EE.............',
      '.......H.....EE.....H.......',
      '.......H############H.......',
      '.......H............H.......',
      '.......H............H.......',
      '..$.œ..H------------H..œ.$..',
      '..######H..........H######..',
      '.......H............H.......',
      '.......H............H.......',
      '.1.$z.$H..$..3...$..H$.z$.2.',
      '.########^........^########.',
      '............................',
      '............................',
      '............................',
    ],
  },
  {
    // TROIS MARCHES, de plus en plus étroites et de plus en plus riches. Monter rapporte, mais
    // chaque étage rapproche du bord.
    slug: 'r3-les-gradins',
    title: 'Ring : les gradins',
    world: 'grotte',
    rows: [
      '.............EE.............',
      '.............EE.............',
      '.........H$..EE..$H.........',
      '.........H########H.........',
      '.........H........H.........',
      '.........H........H.........',
      '....$.$..H........H..$.$....',
      '..#######H........H#######..',
      '.........H........H.........',
      '.........H........H.........',
      '.1z$.œ$..H.$.3..$.H..$œ.$z2.',
      '.##########################.',
      '............................',
      '............................',
      '............................',
      '............................',
    ],
  },
]

// --- ORDRE DU PARCOURS (25/07, audit de rétention) --------------------------------------------
// Mesure sur la base de prod : 01 et 02 finis par 7 joueurs sur 7, 03 par 6, 04 à 07 par 5, puis
// PLUS RIEN. « 08-grande-carte » (une DÉMO technique de la caméra, 56×32) n'avait JAMAIS été fini
// par personne et servait de bouchon : les trois niveaux suivants n'existaient que pour un joueur.
// On sort donc les deux GRANDES CARTES du parcours (elles passent en fin, hors numérotation), et
// on remet les deux cartes moyennes dans l'ordre de leur difficulté MESURÉE (meilleur temps :
// manoir 2:14 avant épave 3:28).
//
// ⚠️ Les SLUGS ne changent pas : les records de la base (scores.level_slug) restent valides.
// (L'appariement avec `levels/test/*.txt` se fait désormais PAR SLUG, plus par position : la
// contrainte « les 5 premiers dans l'ordre alphabétique des fichiers » interdisait d'insérer une
// étape au milieu du monde 1, ce qui est précisément ce que ce parcours demande.)
export const PARCOURS_ORDER: readonly string[] = [
  '01-premiers-pas', '02-la-corde-raide', '03-creuse',
  // MONDE DU CHANTIER (30/07) : « Le piège » apprend à coincer un moine dans un trou creusé —
  // ce que le boss « Les moines fous » exige et que rien n'enseignait.
  'c1-le-piege', '05-les-moines-fous',
  // MONDE DES GLISSADES (30/07) : la glace SEULE puis le sable SEUL, AVANT la carte qui les
  // mélange. « Glace et sable » lâchait les deux d'un coup, plus un marteau et deux moines.
  'g1-patinoire', 'g2-sables-mouvants', '04-glace-et-sable',
  '06-arene-face-a-face', '07-arene-souterraine',
  // MONDE DE L'USINE (31/07) : le biome `usine` ne servait qu'à une carte bonus. Tapis,
  // conduits montants, ressorts et rampes — une trentaine de mécaniques dormaient dans le
  // moteur pendant que le jeu en montrait une poignée.
  'u1-tapis-roulant', 'u2-le-monte-charge', 'u3-les-ressorts', 'u4-la-chaine', 'u5-le-broyeur',
  // MONDE DES MIRAGES (31/07) : les téléporteurs, jamais joués jusqu'ici.
  't1-le-passage', 't2-deux-portes', 't3-le-puits', 't4-le-labyrinthe', 't5-le-mirage',
  // MONDE DES SERRURES (31/07) : portes et clés, jamais jouées jusqu'ici.
  's1-la-cle', 's2-deux-serrures', 's3-le-trousseau', 's4-le-donjon', 's5-le-gardien',
  // LA GRANDE FERME (31/07) : les sens uniques, et le dernier monde du parcours.
  'f1-le-portillon', 'f2-le-detour', 'f3-l-enclos', 'f4-le-troupeau', 'f5-le-grand-pre',
  // MONDE DU MANOIR (30/07) : quatre marches avant le manoir hanté, qu'UN SEUL joueur avait fini.
  // Rien n'apprenait la lave, le feu ni les moines armés — on les découvrait tous d'un coup.
  'm1-coulees', 'm2-le-marteau', 'm3-meche-courte', 'm4-face-a-face', '10-manoir-hante',
  // MONDE DE L'EAU (30/07) : les quatre marches qui manquaient avant l'épave. Mesuré avant de les
  // écrire : cinq joueurs finissaient l'arène souterraine, UN SEUL l'épave. Il n'y avait rien
  // entre les deux.
  'a1-premiere-apnee', 'a2-de-bulle-en-bulle', 'a3-le-siphon', 'a4-plus-profond',
  '09-epave-engloutie',
  // LA FOIRE AUX FARCES (13/08) : la neuvième île, bâtie sur les objets du versus.
  'x1-la-masse', 'x2-le-faux-moine', 'x3-le-drapeau', 'x4-la-couronne', 'x5-la-grande-farce',
  // Hors parcours : grandes cartes d'exploration (56×32 et 42×24), à visiter, pas à franchir.
  '08-grande-carte', '11-grande-machine',
  // Carte de TEST des pièges (31/07) : présentée à part, comme les grandes cartes.
  'p1-le-piege-complet',
  // LES CINQ ARÈNES DU VERSUS (06/08) : rangées en toute fin, hors numérotation du parcours.
  // Elles se jouent à deux ou à trois — les placer au milieu des étapes solo laisserait croire
  // qu'il faut les « finir » pour avancer, alors qu'une arène ne se finit pas, elle se gagne.
  'v1-le-grand-bain', 'v2-la-tour-des-ressorts', 'v3-l-usine', 'v4-la-fosse', 'v5-l-arsenal',
  // Les ARÈNES DE COMBAT (06/08) : on n'y court pas après l'or, on s'y éjecte.
  'r1-le-ring', 'r2-les-deux-iles', 'r3-les-gradins',
]

/** Les arènes de DUEL : jouables en solo comme n'importe quelle carte, mais ce n'est pas leur
 *  raison d'être — elles ne comptent donc pas comme des étapes du parcours (ni numérotées, ni
 *  attendues dans l'ordre de difficulté mesurée). Séparé de BONUS_SLUGS, qui désigne les grandes
 *  cartes d'exploration : ce ne sont pas les mêmes objets, et le joueur ne doit pas les confondre. */
export const ARENA_SLUGS: ReadonlySet<string> = new Set([
  'v1-le-grand-bain', 'v2-la-tour-des-ressorts', 'v3-l-usine', 'v4-la-fosse', 'v5-l-arsenal',
  'r1-le-ring', 'r2-les-deux-iles', 'r3-les-gradins',
])

/** Vrai si ce slug est une arène de duel (versus/coop) plutôt qu'une étape du parcours. */
export function isArenaLevel(slug: string): boolean {
  return ARENA_SLUGS.has(slug)
}

/** Les grandes cartes : jouables, mais présentées à part (pas un palier du parcours). */
export const BONUS_SLUGS: ReadonlySet<string> = new Set(['08-grande-carte', '11-grande-machine', 'p1-le-piege-complet'])

/** Vrai si ce slug est une grande carte d'exploration plutôt qu'une étape du parcours. */
export function isBonusLevel(slug: string): boolean {
  return BONUS_SLUGS.has(slug)
}

/** Le catalogue DANS L'ORDRE JOUÉ (parcours d'abord, grandes cartes à la fin). */
export const TEST_LEVELS: CatalogEntry[] = PARCOURS_ORDER.map((slug) => {
  const e = CATALOG_RAW.find((l) => l.slug === slug)
  if (!e) throw new Error(`PARCOURS_ORDER cite un slug inconnu : ${slug}`)
  return e
})
